import { useEffect, useState } from 'react'

export interface OurListingItem {
  mls: string | null
  address: string
  city?: string
  subdivision: string | null
  price: string
  beds: number | null
  baths: number | null
  halfBaths?: number | null
  sqft: number | null
  status: 'Active' | 'Coming Soon' | 'Pending' | 'Active Under Contract' | 'Sold' | 'Off Market'
  photoUrl: string
  detailUrl: string
  agents: string[]
  soldDate?: string
  soldPrice?: number
  /** ISO date the worker first saw this listing (drives the 14-day NEW ribbon, F.5) */
  firstSeen?: string
}

/** F.5 status → card ribbon. Returns null when no ribbon should render. */
export function ourListingRibbon(
  listing: OurListingItem,
): { label: string; tone: 'gold' | 'ink' | 'muted' } | null {
  switch (listing.status) {
    case 'Pending':
      return { label: 'Pending', tone: 'ink' }
    case 'Active Under Contract':
      return { label: 'Under Contract', tone: 'ink' }
    case 'Coming Soon':
      return { label: 'Coming Soon', tone: 'ink' }
    case 'Sold':
      return { label: 'Sold', tone: 'muted' }
    case 'Active': {
      if (!listing.firstSeen) return null
      const ageDays = (Date.now() - new Date(listing.firstSeen).getTime()) / 86_400_000
      return ageDays <= 14 ? { label: 'New', tone: 'gold' } : null
    }
    default:
      return null
  }
}

type RawOurListingItem = Omit<OurListingItem, 'price'> & {
  price: string | number
  priceDisplay?: string
}

interface OurListingsPayload {
  listings: RawOurListingItem[]
  stale?: boolean
  updatedAt?: string
}

const LIVE_LISTINGS_URL = `${(
  (import.meta.env.VITE_JL_API_BASE as string | undefined) ||
  'https://debeach-api.klingo1188.workers.dev'
).replace(/\/$/, '')}/api/our-listings`

const normalizeListings = (listings: RawOurListingItem[]): OurListingItem[] => listings.map((listing) => ({
  ...listing,
  city: listing.city?.trim(),
  subdivision: listing.subdivision?.trim() || null,
  price: listing.priceDisplay || (typeof listing.price === 'number' ? `$${listing.price.toLocaleString('en-US')}` : listing.price),
  photoUrl: listing.photoUrl || '/media/listing-placeholder.svg',
}))

/**
 * Team listings (Parity V2 F.7 step 3): live Worker feed once deployed,
 * falling back to the static snapshot at /data/our-listings.json.
 */
export function useOurListings(): { listings: OurListingItem[]; stale: boolean; loading: boolean } {
  const [listings, setListings] = useState<OurListingItem[]>([])
  const [stale, setStale] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ctrl = new AbortController()
    const load = async () => {
      try {
        const res = await fetch(LIVE_LISTINGS_URL, { signal: ctrl.signal })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as OurListingsPayload
        if (!data.listings?.length) throw new Error('empty feed')
        setListings(normalizeListings(data.listings))
        setStale(!!data.stale)
      } catch {
        if (ctrl.signal.aborted) return
        try {
          const res = await fetch('/data/our-listings.json', { signal: ctrl.signal })
          const data = (await res.json()) as OurListingsPayload
          setListings(normalizeListings(data.listings ?? []))
          setStale(true)
        } catch {
          if (!ctrl.signal.aborted) setListings([])
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }
    load()
    return () => ctrl.abort()
  }, [])

  return { listings, stale, loading }
}
